import { createAxiosInstance } from '../utils/axios.js'

const translationApi = createAxiosInstance()

const TRANSLATION_CACHE_KEY = 'translation_cache'
const CACHE_EXPIRY = 24 * 60 * 60 * 1000

let translationCache = {}
const originalTextMap = new WeakMap()

function loadCache () {
  try {
    const cached = localStorage.getItem(TRANSLATION_CACHE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached)
      if (parsed.expiry > Date.now()) {
        translationCache = parsed.data || {}
      }
    }
  } catch (e) {
    console.warn('Failed to load translation cache:', e)
  }
}

function saveCache () {
  try {
    localStorage.setItem(
      TRANSLATION_CACHE_KEY,
      JSON.stringify({
        data: translationCache,
        expiry: Date.now() + CACHE_EXPIRY
      })
    )
  } catch (e) {
    console.warn('Failed to save translation cache:', e)
  }
}

loadCache()

function decodeHtmlEntities (text) {
  if (!text) return text
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  const decoded = textarea.value
  textarea.remove()
  return decoded
}

export const translationService = {
  currentLanguage: localStorage.getItem('preferred_language') || 'en',

  async getSupportedLanguages (countryCode = null) {
    try {
      const url = countryCode
        ? `/api/languages?country_code=${countryCode}`
        : '/api/languages'
      const response = await translationApi.get(url)
      const data = response.data

      if (data.all) {
        return data
      }

      return { all: data, regional: [], country_code: null }
    } catch (error) {
      console.error('Failed to fetch supported languages:', error)
      return { all: { en: 'English' }, regional: ['en'], country_code: null }
    }
  },

  async detectRegion () {
    try {
      const response = await translationApi.get('/api/detect-region')
      return response.data?.country_code || null
    } catch (error) {
      console.warn('Failed to detect region:', error)
      return null
    }
  },

  setLanguage (lang) {
    const oldLanguage = this.currentLanguage
    this.currentLanguage = lang
    localStorage.setItem('preferred_language', lang)
    document.documentElement.lang = lang
    window.dispatchEvent(
      new CustomEvent('language-changed', {
        detail: { language: lang, oldLanguage }
      })
    )
  },

  getLanguage () {
    return this.currentLanguage
  },

  async preTranslateCommonStrings (targetLang = null) {
    const lang = targetLang || this.currentLanguage
    if (lang === 'en') return

    const commonStrings = [
      'Translating the page...',
      'Changing language...',
      'Loading page...',
      'Loading...',
      'Books',
      'Authors',
      'Events',
      'Submission',
      'Press',
      'Careers',
      'Admin',
      'Account Settings',
      'Purchase History',
      'Admin Dashboard',
      'Menu',
      'Logout',
      'Login',
      'Register',
      'About',
      'Search book, author, series, ISBN...',
      'Search menu items...',
      'No menu items found',
      'Admin Dashboard',
      // Login/Register page
      'Sign in to your account',
      'Sign in',
      'Signing in...',
      'Email address',
      'Password',
      'Forgot password?',
      'Do not have an account?',
      'Create your account',
      'Creating account...',
      'Create account',
      'Already have an account?',
      'First Name',
      'Last Name',
      'Phone Number',
      'Confirm Password',
      // Cart page
      'Shopping Cart',
      'Your cart is empty',
      'Continue Shopping',
      'Ticket Holder',
      'Remove',
      'Quantity',
      'Subtotal',
      'Total',
      'Proceed to Checkout',
      'Discount',
      // Checkout page
      'Checkout',
      'Shipping',
      'Delivery',
      'Payment',
      'Contact Information',
      'Shipping Information',
      'Email',
      'Phone Number',
      'Name',
      'Address',
      'City',
      'State',
      'Zip Code',
      'Country',
      'Place Order',
      'Processing...',
      'Save',
      'Cancel',
      'Edit',
      'Delete',
      'Close',
      'Open',
      'Next',
      'Previous',
      'Back',
      'Error',
      // Checkout success
      'Payment Successful!',
      'Payment Failed',
      'Thank you for your purchase',
      'Order Number',
      'Return to Cart',
      'View Orders',
      // Purchase history
      'Purchase History',
      'No purchases yet',
      'Purchases',
      'Book',
      'Author',
      'Format',
      'Price',
      'Purchase Date',
      'Actions',
      'Order',
      'Purchased',
      'Download',
      'View Receipt',
      // Search
      'Find a book or author',
      'Search',
      // Common messages
      'Loading page...',
      'Processing your payment...',
      'Error',
      'Success'
    ]

    const stringsToTranslate = commonStrings.filter(text => {
      const cacheKey = `en-${lang}-${text}`
      return !translationCache[cacheKey]
    })

    if (stringsToTranslate.length > 0) {
      try {
        const translations = await this.translateBulk(stringsToTranslate, lang)
        stringsToTranslate.forEach((text, index) => {
          const cacheKey = `en-${lang}-${text}`
          translationCache[cacheKey] = translations[index]
        })
        saveCache()
      } catch (error) {
        console.warn('Failed to pre-translate common strings:', error)
      }
    }
  },

  getCachedTranslation (text, targetLang = null) {
    const lang = targetLang || this.currentLanguage
    if (lang === 'en' || !text || text.trim() === '') {
      return text
    }

    const cacheKey = `en-${lang}-${text}`
    if (translationCache[cacheKey]) {
      return decodeHtmlEntities(translationCache[cacheKey])
    }

    return text
  },

  async translateText (text, targetLang = null) {
    const lang = targetLang || this.currentLanguage
    if (lang === 'en' || !text || text.trim() === '') {
      return text
    }

    const sourceLang = 'en'
    const cacheKey = `${sourceLang}-${lang}-${text}`
    if (translationCache[cacheKey]) {
      return decodeHtmlEntities(translationCache[cacheKey])
    }

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Translation request timeout')),
          10000
        )
      })

      const response = await Promise.race([
        translationApi.post('/api/translate', {
          text,
          target_lang: lang,
          source_lang: sourceLang
        }),
        timeoutPromise
      ])

      let translated = response.data?.translated_text || text
      translated = decodeHtmlEntities(translated)
      translationCache[cacheKey] = translated
      saveCache()
      return translated
    } catch (error) {
      console.warn('Translation failed, using original text:', error)
      return text
    }
  },

  async translateBulk (texts, targetLang = null) {
    const lang = targetLang || this.currentLanguage
    if (lang === 'en' || !texts || texts.length === 0) {
      return texts
    }

    const sourceLang = 'en'
    const textsToTranslate = []
    const textIndices = []
    const results = new Array(texts.length)

    texts.forEach((text, index) => {
      if (!text || text.trim() === '') {
        results[index] = text
        return
      }

      const cacheKey = `${sourceLang}-${lang}-${text}`
      if (translationCache[cacheKey]) {
        results[index] = decodeHtmlEntities(translationCache[cacheKey])
      } else {
        textsToTranslate.push(text)
        textIndices.push(index)
      }
    })

    if (textsToTranslate.length === 0) {
      return results
    }

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Bulk translation request timeout')),
          60000
        )
      })

      const response = await Promise.race([
        translationApi.post('/api/translate/bulk', {
          texts: textsToTranslate,
          target_lang: lang,
          source_lang: sourceLang
        }),
        timeoutPromise
      ])

      const translatedTexts =
        response.data?.translated_texts || textsToTranslate

      translatedTexts.forEach((translated, idx) => {
        const originalText = textsToTranslate[idx]
        const decoded = decodeHtmlEntities(translated)
        const cacheKey = `${sourceLang}-${lang}-${originalText}`
        translationCache[cacheKey] = decoded
        results[textIndices[idx]] = decoded
      })

      saveCache()
      return results
    } catch (error) {
      console.warn('Bulk translation failed, using original texts:', error)
      textsToTranslate.forEach((text, idx) => {
        results[textIndices[idx]] = text
      })
      return results
    }
  },

  /**
   * Detect the language of each text in the provided array.
   * Returns an array of BCP-47 language codes (e.g. "fr", "en").
   * Results are not cached because detection is only called for
   * book descriptions which are relatively small in number.
   */
  async detectLanguageBulk (texts) {
    if (!texts || texts.length === 0) return []

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Language detection timeout')),
          10000
        )
      })

      const response = await Promise.race([
        translationApi.post('/api/detect-language', { texts }),
        timeoutPromise
      ])

      return response.data?.detected_languages || texts.map(() => 'en')
    } catch (error) {
      console.warn('Language detection failed, assuming English:', error)
      return texts.map(() => 'en')
    }
  },

  /**
   * Smart-translate an array of texts to `targetLang`.
   * Each text is first auto-detected by the backend; if the detected
   * language already matches `targetLang` the original text is returned
   * unchanged.  Results are cached using a key that includes the detected
   * source language so future calls are fast.
   *
   * @param {string[]} texts
   * @param {string|null} targetLang  defaults to the current UI language
   * @returns {Promise<Array<{translatedText: string, detectedLanguage: string, wasTranslated: boolean}>>}
   */
  async smartTranslateTexts (texts, targetLang = null) {
    const lang = targetLang || this.currentLanguage
    if (!texts || texts.length === 0) return []

    const results = new Array(texts.length)
    const textsToTranslate = []
    const indicesToTranslate = []

    texts.forEach((text, i) => {
      if (!text || !text.trim()) {
        results[i] = { translatedText: text || '', detectedLanguage: 'en', wasTranslated: false }
        return
      }

      // Check cache: we store smart results with a special prefix
      const cacheKey = `smart-${lang}-${text}`
      if (translationCache[cacheKey]) {
        const cached = translationCache[cacheKey]
        results[i] = {
          translatedText: decodeHtmlEntities(cached.translatedText),
          detectedLanguage: cached.detectedLanguage,
          wasTranslated: cached.wasTranslated
        }
      } else {
        textsToTranslate.push(text)
        indicesToTranslate.push(i)
      }
    })

    if (textsToTranslate.length === 0) return results

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Smart translation timeout')),
          30000
        )
      })

      const response = await Promise.race([
        translationApi.post('/api/translate/smart', {
          texts: textsToTranslate,
          target_lang: lang
        }),
        timeoutPromise
      ])

      const apiResults = response.data?.results || []

      apiResults.forEach((r, idx) => {
        const originalIndex = indicesToTranslate[idx]
        const originalText = textsToTranslate[idx]
        const translated = decodeHtmlEntities(r.translated_text)
        const result = {
          translatedText: translated,
          detectedLanguage: r.detected_language || 'en',
          wasTranslated: r.was_translated || false
        }
        results[originalIndex] = result

        // Cache the result
        const cacheKey = `smart-${lang}-${originalText}`
        translationCache[cacheKey] = {
          translatedText: r.translated_text,
          detectedLanguage: r.detected_language || 'en',
          wasTranslated: r.was_translated || false
        }
      })

      saveCache()
    } catch (error) {
      console.warn('Smart translation failed, using original texts:', error)
      textsToTranslate.forEach((text, idx) => {
        results[indicesToTranslate[idx]] = {
          translatedText: text,
          detectedLanguage: 'en',
          wasTranslated: false
        }
      })
    }

    return results
  },

  async revertToEnglish (element) {
    const textNodes = []
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    )
    let node
    while ((node = walker.nextNode())) {
      const originalText = originalTextMap.get(node)
      if (originalText && node.textContent !== originalText) {
        textNodes.push({ node, originalText })
      }
    }
    textNodes.forEach(({ node, originalText }) => {
      node.textContent = originalText
      originalTextMap.delete(node)
    })

    const inputs = element.querySelectorAll(
      'input[placeholder][data-original-placeholder], textarea[placeholder][data-original-placeholder]'
    )
    inputs.forEach(input => {
      const originalPlaceholder = input.getAttribute(
        'data-original-placeholder'
      )
      if (originalPlaceholder != null) {
        input.setAttribute('placeholder', originalPlaceholder)
        input.removeAttribute('data-original-placeholder')
      }
    })
  },

  async translateElement (element) {
    if (this.currentLanguage === 'en') {
      await this.revertToEnglish(element)
      return
    }

    const skipTags = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']
    const skipSelectors = [
      '[data-no-translate]',
      '.no-translate',
      '[contenteditable]',
      '[role="textbox"]',
      '.ql-editor',
      '.ql-container',
      'select',
      'option',
      'input',
      'textarea'
    ]

    const shouldSkip = node => {
      if (!node.parentElement) return true

      const tagName = node.parentElement.tagName
      if (skipTags.includes(tagName)) return true

      for (const selector of skipSelectors) {
        if (node.parentElement.closest(selector)) return true
      }

      if (node.parentElement.hasAttribute('data-no-translate')) return true
      if (node.parentElement.classList.contains('no-translate')) return true

      return false
    }

    const companyNames = [
      'Barry & Bonna Publishing',
      'Barry & Bonna',
      'Barry and Bonna Publishing',
      'Barry and Bonna'
    ]

    const currencyPattern =
      /^\$?\d+\.?\d*\s*(USD|usd)?$|^\$[\d,]+\.?\d*$|USD|usd/

    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: node => {
          if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT
          if (shouldSkip(node)) return NodeFilter.FILTER_REJECT
          const text = node.textContent.trim()
          if (companyNames.some(name => text.includes(name)))
            return NodeFilter.FILTER_REJECT
          if (currencyPattern.test(text)) return NodeFilter.FILTER_REJECT
          return NodeFilter.FILTER_ACCEPT
        }
      },
      false
    )

    const textMap = new Map()
    let node
    while ((node = walker.nextNode())) {
      const text = node.textContent.trim()
      if (text && text.length > 0) {
        const containsCompanyName = companyNames.some(name =>
          text.includes(name)
        )
        const containsCurrency = currencyPattern.test(text)
        if (!containsCompanyName && !containsCurrency) {
          if (!textMap.has(text)) {
            textMap.set(text, [])
          }
          textMap.get(text).push(node)
        }
      }
    }

    const placeholderMap = new Map()
    const inputs = element.querySelectorAll(
      'input[placeholder], textarea[placeholder]'
    )
    inputs.forEach(input => {
      const placeholder = input.getAttribute('placeholder')
      if (
        placeholder &&
        placeholder.trim() &&
        !input.closest('[data-no-translate]') &&
        !input.closest('.no-translate')
      ) {
        if (!placeholderMap.has(placeholder)) {
          placeholderMap.set(placeholder, [])
        }
        placeholderMap.get(placeholder).push(input)
      }
    })

    const allTextsToTranslate = [
      ...Array.from(textMap.keys()),
      ...Array.from(placeholderMap.keys())
    ]

    if (allTextsToTranslate.length === 0) {
      return
    }

    const allTranslations = await this.translateBulk(allTextsToTranslate)
    const textTranslations = allTranslations.slice(0, textMap.size)
    const placeholderTranslations = allTranslations.slice(textMap.size)

    const uniqueTexts = Array.from(textMap.keys())
    uniqueTexts.forEach((currentText, index) => {
      const translated = textTranslations[index]
      const nodes = textMap.get(currentText)
      if (nodes && translated !== currentText) {
        nodes.forEach(node => {
          if (!originalTextMap.has(node)) {
            originalTextMap.set(node, currentText)
          }
          node.textContent = node.textContent.replace(currentText, translated)
        })
      }
    })

    const placeholderTexts = Array.from(placeholderMap.keys())
    placeholderTexts.forEach((originalPlaceholder, index) => {
      const translated = placeholderTranslations[index]
      if (translated !== originalPlaceholder) {
        const inputs = placeholderMap.get(originalPlaceholder)
        inputs.forEach(input => {
          if (
            !input.hasAttribute('data-original-placeholder') &&
            input.getAttribute('placeholder') === originalPlaceholder
          ) {
            input.setAttribute('data-original-placeholder', originalPlaceholder)
          }
          input.setAttribute('placeholder', translated)
        })
      }
    })
  }
}
